/* Author Page — `/bookstore/author/:slug`.
 * Public page for a single bookstore pen name: portrait, bio, and
 * every visible book they've written, reusing the same BookCard /
 * BookDetailModal pattern as GenrePage.jsx.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { bookstoreApi } from '@/lib/bookstoreApi';
import BookCard from '@/components/bookstore/BookCard';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import AdultsOnlyGate, { hasAcceptedAdultsOnly } from '@/components/bookstore/AdultsOnlyGate';
import GenreContentWarningDialog, { hasAcceptedContentWarning } from '@/components/bookstore/GenreContentWarningDialog';

export default function BookstoreAuthorPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(null);
  // iter-deeplink-gate (author page) — re-evaluated on every bump,
  // same pattern GenrePage uses so a shared author link can't be used
  // to skip the 18+ checklist / content-warning dialog.
  const [, setGateTick] = useState(0);
  const bumpGate = () => setGateTick((n) => n + 1);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    bookstoreApi.authorPage(slug)
      .then((d) => {
        setData(d);
        // Inject Microsoft Clarity if the author has configured it
        const cid = d?.clarity_project_id;
        if (cid && !document.getElementById('ms-clarity-script')) {
          const s = document.createElement('script');
          s.id = 'ms-clarity-script';
          s.type = 'text/javascript';
          s.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${cid}");`;
          document.head.appendChild(s);
        }
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error('Could not load this author right now. Please try again.');
      });
  }, [slug]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const portraitUrl = data?.author?.portrait_url
    ? (data.author.portrait_url.startsWith('http')
        ? data.author.portrait_url
        : `${backendUrl}${data.author.portrait_url}`)
    : null;

  // Build a richer meta description: bio + genre/book count context.
  const seoDescription = (() => {
    if (!data) return undefined;
    const bio   = (data.author.bio || '').slice(0, 120);
    const books = data.books?.length ?? 0;
    const genreNames = (data.genres || []).slice(0, 2).map((g) => g.name).join(' & ');
    const suffix = [
      books > 0 && `${books} book${books === 1 ? '' : 's'}`,
      genreNames && genreNames,
    ].filter(Boolean).join(' · ');
    return suffix ? `${bio} — ${suffix}` : bio || undefined;
  })();

  useSeoMeta(`/bookstore/author/${slug}`, {
    title: data ? `${data.author.pen_name} — Crown & Rose` : 'Author — Crown & Rose',
    description: seoDescription,
    og_image:    portraitUrl,
    og_type:     'profile',
  });

  const genres = data?.genres || [];
  const needsAdultsOnlyGate = genres.some((g) => g.parent_group === 'adults-only') && !hasAcceptedAdultsOnly();
  const warnGenre = !needsAdultsOnlyGate
    ? genres.find((g) => g.content_warning && !hasAcceptedContentWarning(g.slug))
    : null;
  const gatePassed = !data || (!needsAdultsOnlyGate && !warnGenre);

  // iter-deeplink-gate-checklist — Only fetch the parent's custom
  // checklist when it's actually needed, mirroring GenrePage.
  const [parentChecklist, setParentChecklist] = useState(undefined);
  useEffect(() => {
    if (needsAdultsOnlyGate) {
      bookstoreApi.booksByGenre('adults-only')
        .then((r) => setParentChecklist(r?.genre?.gate_checklist))
        .catch(() => {});
    }
  }, [needsAdultsOnlyGate]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-foreground" data-testid="bs-author-not-found">
        <p className="text-sm text-muted-foreground">We couldn't find that author.</p>
        <button onClick={() => navigate('/bookstore')} className="text-sm text-primary underline underline-offset-4">
          Back to the bookstore
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="bs-author-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { author, books } = data;

  return (
    <div className="min-h-screen bg-background text-foreground pb-[max(6rem,env(safe-area-inset-bottom))]" data-testid={`bs-author-page-${slug}`}>
      <AdultsOnlyGate
        open={needsAdultsOnlyGate}
        checklist={parentChecklist}
        onAccept={bumpGate}
        onRetreat={() => navigate('/bookstore')}
      />
      <GenreContentWarningDialog
        genre={(!needsAdultsOnlyGate && warnGenre) ? warnGenre : null}
        onAcceptOnce={bumpGate}
        onRetreat={() => navigate('/bookstore')}
      />
      <div className="border-b border-border/60 bg-card/40">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <button
            onClick={() => {
              // Direct visits (shared link / new tab) have no in-app history
              // to go back to — `navigate(-1)` would leave the site entirely.
              if (location.key === 'default' || window.history.length <= 1) navigate('/bookstore');
              else navigate(-1);
            }}
            data-testid="bs-author-back-btn"
            className="inline-flex items-center gap-1 text-[11.5px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <Link to="/bookstore" data-testid="bs-author-all-genres-link" className="text-[11.5px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
            All genres
          </Link>
        </div>
      </div>

      {!gatePassed ? (
        <div className="flex h-72 items-center justify-center text-muted-foreground" data-testid={`bs-author-gate-pending-${slug}`}>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking access…
        </div>
      ) : (
      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
          {author.portrait_url && (
            <img
              src={author.portrait_url}
              alt={author.pen_name}
              data-testid="bs-author-portrait"
              className="h-28 w-28 shrink-0 rounded-full border border-border object-cover sm:h-32 sm:w-32"
            />
          )}
          <div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="bs-author-name">
              {author.pen_name}
            </h1>
            {author.bio && (
              <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground" data-testid="bs-author-bio">
                {author.bio}
              </p>
            )}
            <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              {books.length} {books.length === 1 ? 'book' : 'books'}
            </p>
          </div>
        </div>

        <div className="mt-10 mb-6">
          <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Books by {author.pen_name}</h2>
          {books.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-6 text-[13px] text-muted-foreground">
              No books published under this name yet.
            </p>
          ) : (
            <div className="grid grid-cols-2 justify-items-center gap-x-4 gap-y-16 sm:grid-cols-3 lg:grid-cols-4" data-testid="bs-author-books-grid">
              {books.map((b) => (
                <BookCard key={b.id} book={b} onClick={setOpen} />
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {open && <BookDetailModal book={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
